// ---------------------------------------------------------------------------
// Creator-facing copy for a chat-dispatched tool that failed — a CLOSED switch
// over recognized failures.
//
// WHY THIS EXISTS — production defect, 2026-08-04 (www.cogo.xyz/studio). When
// a chat-dispatched action failed, `chat-model-adapter` appended the engine's
// own string straight into the assistant's message:
//
//     fullText += `\n\n[MCP Error: ${result.error}]`
//
// so the creator read `[MCP Error: Invalid input for Studio operation …]` —
// the string `validateStudioOperationInput` builds for ENGINEERS, naming an
// internal operation id and a JSON-schema path. It is unreadable, it is
// unactionable, and in a narrow chat column it is clipped mid-word.
//
// The constraint here is the same one planning-failure-copy.ts enforces:
// `resolveChatToolFailureReason` only ever returns a member of a closed union
// (or null), and `chatToolFailureMessage` is TOTAL over that union — every
// branch returns authored copy. An unrecognized message maps to `generic`.
// There is deliberately no path that returns, embeds, or interpolates the raw
// text, so a new engine string added anywhere in the gateway degrades to the
// generic line instead of leaking.
//
// Matching is on markers this app AUTHORS itself (studio-capability-gateway.ts
// and chat-model-adapter.ts) — never on model output.
// ---------------------------------------------------------------------------

/** Failures the chat surface can say something specific about. */
export type ChatToolFailureReason =
  | 'invalid-input'
  | 'not-permitted'
  | 'unknown-action'

/** The subset of `strings.chat.toolFailure` this module needs. */
export interface ChatToolFailureCopy {
  invalidInput: string
  notPermitted: string
  unknownAction: string
  generic: string
}

/**
 * Markers, each authored by this app at a known site:
 *  - `validateStudioOperationInput`  (studio-capability-gateway.ts)
 *  - `executeCliAction`              (chat-model-adapter.ts)
 *  - `executeStudioChatOperationOnce`(studio-capability-gateway.ts)
 */
const FAILURE_MARKERS: readonly (readonly [string, ChatToolFailureReason])[] = [
  ['Invalid input for Studio operation', 'invalid-input'],
  ['Studio action requires an operation contract', 'not-permitted'],
  ['Unknown Studio operation', 'unknown-action'],
  ['Unsupported Studio action', 'unknown-action'],
]

/**
 * Recover the reason from a raw failure string, or `null` when it is not one
 * this surface has authored copy for.
 */
export function resolveChatToolFailureReason(
  raw: string | null | undefined,
): ChatToolFailureReason | null {
  if (typeof raw !== 'string' || !raw.trim()) return null
  for (const [marker, reason] of FAILURE_MARKERS) {
    if (raw.includes(marker)) return reason
  }
  return null
}

/**
 * The line the creator reads. Total by construction: recognized reasons get
 * their authored copy, everything else gets `generic`. The raw text is never
 * part of the return value — callers log it instead.
 */
export function chatToolFailureMessage(
  raw: string | null | undefined,
  copy: ChatToolFailureCopy,
): string {
  switch (resolveChatToolFailureReason(raw)) {
    case 'invalid-input':
      return copy.invalidInput
    case 'not-permitted':
      return copy.notPermitted
    case 'unknown-action':
      return copy.unknownAction
    default:
      return copy.generic
  }
}
